import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './HerbLibrary.css';
import { HerbPrescription, PrescriptionValidationPayload } from '../types';
import { GENERATED_RECIPE_PREVIEW_KEY } from '../constants/storageKeys';
import apiClient from '../services/apiClient';

interface Herb {
  id: string;
  name: string;
  category: string;
  properties: string;
  flavor: string;
  functions: string[];
  indications: string[];
  dosage: string;
  contraindications?: string;
  description?: string;
}
const PAGE_SIZE = 4;
const VALIDATION_STATUS_LABELS: Record<'pass' | 'warning' | 'fail', string> = {
  pass: '配伍安全',
  warning: '配伍提醒',
  fail: '配伍禁忌',
};

const extractRecipeTitle = (markdown: string, fallback?: string | null): string => {
  const headingMatch = markdown.match(/^#\s*(.+)$/m);
  if (headingMatch?.[1]) {
    return headingMatch[1].trim();
  }

  const firstContentLine = markdown
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0 && !line.startsWith('!['));

  if (firstContentLine) {
    return firstContentLine.replace(/^[-*]\s*/, '').trim();
  }

  return fallback?.trim() || 'AI 药膳方案';
};

const HerbLibrary: React.FC = () => {
  const log = useCallback((message: string, payload?: Record<string, unknown>) => {
    if (payload) {
      console.debug(`[HerbLibrary] ${message}`, payload);
    } else {
      console.debug(`[HerbLibrary] ${message}`);
    }
  }, []);

  const [herbs, setHerbs] = useState<Herb[]>([]);
  const [filteredHerbs, setFilteredHerbs] = useState<Herb[]>([]);
  const [selectedHerbs, setSelectedHerbs] = useState<Herb[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [effectKeyword, setEffectKeyword] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);
  const [generatedPrescription, setGeneratedPrescription] = useState<HerbPrescription | null>(null);
  const [isConvertingRecipe, setIsConvertingRecipe] = useState(false);
  const [hasRecipePreview, setHasRecipePreview] = useState(false);
  const [recipePreviewTitle, setRecipePreviewTitle] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectionValidation, setSelectionValidation] = useState<PrescriptionValidationPayload | null>(null);
  const [selectionValidationMessage, setSelectionValidationMessage] = useState<string | null>(null);
  const [selectionValidationLoading, setSelectionValidationLoading] = useState<boolean>(false);
  const [selectionValidationBlockMessage, setSelectionValidationBlockMessage] = useState<string | null>(null);
  const validationRequestIdRef = useRef(0);

  const [categories, setCategories] = useState<string[]>(['all']);
  const navigate = useNavigate();

  const summarizeValidationMessage = useCallback((payload: PrescriptionValidationPayload | null) => {
    if (!payload) {
      return null;
    }

    const prioritized = payload.messages.find((message) => !message.passed) ?? payload.messages[0];
    if (prioritized?.message) {
      return prioritized.message;
    }

    if (payload.status === 'warning') {
      return '当前配伍存在注意事项，请酌情调整剂量或替换药材。';
    }
    if (payload.status === 'fail') {
      return '当前配伍检测到禁忌，请移除冲突药材。';
    }
    return null;
  }, []);

  const runSelectionValidation = useCallback(
    async (herbList: Herb[]): Promise<{ payload: PrescriptionValidationPayload | null; message: string | null; allow: boolean; requestId: number }> => {
      if (herbList.length === 0) {
        const requestId = validationRequestIdRef.current + 1;
        validationRequestIdRef.current = requestId;
        setSelectionValidationLoading(false);
        return { payload: null, message: null, allow: true, requestId };
      }

      const requestId = validationRequestIdRef.current + 1;
      validationRequestIdRef.current = requestId;
      setSelectionValidationLoading(true);

      const payload = {
        herbs: herbList.map((herb) => ({
          name: herb.name,
          dosage: '',
          reason: herb.functions?.[0] ?? '',
        })),
      };

      try {
        const response = await apiClient.post<PrescriptionValidationPayload>(
          '/herb-library/validate',
          payload,
        );

        const validation = response.data;
        const summary = summarizeValidationMessage(validation);
        return {
          payload: validation,
          message: summary,
          allow: validation.status !== 'fail',
          requestId,
        };
      } catch (err) {
        if (isAxiosError(err)) {
          const detail = err.response?.data?.detail as
            | { validation?: PrescriptionValidationPayload; message?: string }
            | undefined;
          if (detail?.validation) {
            const summary = summarizeValidationMessage(detail.validation) ?? detail.message ?? null;
            return {
              payload: detail.validation,
              message: summary,
              allow: detail.validation.status !== 'fail',
              requestId,
            };
          }
          if (detail?.message) {
            return { payload: null, message: detail.message, allow: false, requestId };
          }
        }

        return {
          payload: null,
          message: '暂时无法校验药材配伍，请稍后重试。',
          allow: false,
          requestId,
        };
      } finally {
        if (validationRequestIdRef.current === requestId) {
          setSelectionValidationLoading(false);
        }
      }
    },
    [summarizeValidationMessage],
  );

  const loadCategories = useCallback(async () => {
    log('loadCategories:start');
    try {
  const response = await apiClient.get<string[]>('/herb_categories');
      const fetchedCategories: string[] = response.data;
      setCategories(['all', ...fetchedCategories]);
      log('loadCategories:success', { count: fetchedCategories.length });
    } catch (err) {
      console.error('加载中药分类失败:', err);
      setCategories([
        'all', '解表药', '清热药', '泻下药', '祛风湿药', '化湿药',
        '利水渗湿药', '温里药', '理气药', '消食药', '止血药',
        '活血化瘀药', '化痰止咳平喘药', '安神药', '平肝息风药',
        '补虚药', '收涩药', '驱虫药', '外用药'
      ]);
      log('loadCategories:error');
    }
  }, [log]);

  const loadHerbs = useCallback(async () => {
    log('loadHerbs:start');
    try {
  const response = await apiClient.get<Herb[]>('/herbs');
      const fetchedHerbs: Herb[] = response.data;

      setHerbs(fetchedHerbs);
      setFilteredHerbs(fetchedHerbs);
      setIsLoading(false);
      log('loadHerbs:success', { count: fetchedHerbs.length });
    } catch (err) {
      console.error('加载中药数据失败:', err);
      setError('加载中药数据失败，请检查网络连接');
      setIsLoading(false);
      log('loadHerbs:error');
    }
  }, [log]);

  useEffect(() => {
    log('component:mount');
    loadHerbs();
    loadCategories();
    return () => {
      log('component:unmount');
    };
  }, [loadCategories, loadHerbs, log]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const cached = window.sessionStorage.getItem(GENERATED_RECIPE_PREVIEW_KEY);
      if (!cached) {
        return;
      }
      const parsed = JSON.parse(cached) as { recipeTitle?: string; title?: string } | null;
      const title = parsed?.recipeTitle || parsed?.title || null;
      if (title) {
        setHasRecipePreview(true);
        setRecipePreviewTitle(title);
      }
    } catch (err) {
      console.warn('读取药膳预览缓存失败', err);
    }
  }, []);
  useEffect(() => {
    let filtered = herbs;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((herb) => herb.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (herb) =>
          herb.name.toLowerCase().includes(query) ||
          herb.functions.some((func) => func.toLowerCase().includes(query)) ||
          herb.indications.some((indication) => indication.toLowerCase().includes(query)),
      );
    }

    setFilteredHerbs(filtered);
    log('filter:update', { filtered: filtered.length, total: herbs.length, searchQuery, selectedCategory });
  }, [herbs, log, searchQuery, selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
    log('pagination:reset', { reason: 'filterChanged' });
  }, [effectKeyword, herbs.length, log, searchQuery, selectedCategory]);

  const heroStats = useMemo(() => [
    { label: '收录药材', value: herbs.length },
    { label: '符合筛选', value: filteredHerbs.length },
    { label: '已选药材', value: selectedHerbs.length },
  ], [herbs.length, filteredHerbs.length, selectedHerbs.length]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredHerbs.length / PAGE_SIZE)),
    [filteredHerbs.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      log('pagination:clamp', { from: currentPage, to: totalPages });
      setCurrentPage(totalPages);
    }
  }, [currentPage, log, totalPages]);

  const paginatedHerbs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    const slice = filteredHerbs.slice(start, end);
    log('pagination:compute', { page: currentPage, pageSize: PAGE_SIZE, slice: slice.length });
    return slice;
  }, [currentPage, filteredHerbs, log]);

  const gotoPage = (page: number) => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(safePage);
    log('pagination:goto', { target: page, safePage });
  };

  const curatedCategories = useMemo(() => {
    const presets = ['补虚药', '解表药', '清热药', '安神药', '化湿药'];
    return presets.filter((preset) => categories.includes(preset));
  }, [categories]);

  const functionCloud = useMemo(() => {
    const counts = new Map<string, number>();
    filteredHerbs.forEach((herb) => {
      herb.functions.forEach((func) => {
        const key = func.trim();
        if (!key) return;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
  }, [filteredHerbs]);

  const handleCategoryPreset = (category: string) => {
    setSelectedCategory(category);
    setEffectKeyword(null);
  };

  const handleEffectKeyword = (keyword: string) => {
    setSearchQuery(keyword);
    setEffectKeyword(keyword);
  };

  const toggleHerbSelection = useCallback(
    async (herb: Herb) => {
      setError(null);
      setSelectionValidationBlockMessage(null);
      const isSelected = selectedHerbs.some((item) => item.id === herb.id);

      if (isSelected) {
        const updated = selectedHerbs.filter((item) => item.id !== herb.id);
        setSelectedHerbs(updated);
        const result = await runSelectionValidation(updated);
        if (result.requestId !== validationRequestIdRef.current) {
          return;
        }
        if (updated.length === 0) {
          setSelectionValidation(null);
          setSelectionValidationMessage(null);
        } else {
          setSelectionValidation(result.payload);
          setSelectionValidationMessage(result.message ?? null);
        }
        setSelectionValidationBlockMessage(null);
        return;
      }

      const previousSelection = [...selectedHerbs];
      const proposed = [...selectedHerbs, herb];
      setSelectedHerbs(proposed);

      const result = await runSelectionValidation(proposed);
      if (result.requestId !== validationRequestIdRef.current) {
        return;
      }

      if (result.allow) {
        setSelectionValidation(result.payload);
        setSelectionValidationMessage(result.message ?? null);
        setSelectionValidationBlockMessage(null);
      } else {
        setSelectedHerbs(previousSelection);
        const blockMessage = result.message ?? '所选药材存在禁忌，请调整后再试';
        setSelectionValidationMessage(null);
        setSelectionValidationBlockMessage(blockMessage);
        setError(blockMessage);
      }
    },
    [runSelectionValidation, selectedHerbs],
  );

  const generatePrescription = async () => {
    if (selectedHerbs.length === 0) {
      setError('请至少选择一味中药');
      return;
    }

    if (selectionValidationLoading) {
      setError('正在校验药材配伍，请稍候再生成处方');
      return;
    }

    if (selectionValidation?.status === 'fail') {
      setError(selectionValidationMessage ?? '当前配伍存在禁忌，请调整后再生成处方');
      return;
    }

    setPrescriptionLoading(true);
    setError(null);
    setGeneratedPrescription(null);
    setHasRecipePreview(false);
    setRecipePreviewTitle(null);
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(GENERATED_RECIPE_PREVIEW_KEY);
      } catch (storageError) {
        console.warn('清理药膳预览缓存失败', storageError);
      }
    }

    try {
      const herbPayload = selectedHerbs.map((herb) => ({
        name: herb.name,
        dosage: '',
        reason: herb.functions?.[0] || '',
      }));

      const notes = selectedHerbs
        .map((herb) => `${herb.name}：${herb.functions.slice(0, 2).join('，')}`)
        .join('；');

      const response = await apiClient.post<HerbPrescription>(
        '/herb-library/prescriptions',
        {
          herbs: herbPayload,
          notes: notes || undefined,
        }
      );

      setGeneratedPrescription(response.data);
      if (response.data.validation) {
        setSelectionValidation(response.data.validation);
        setSelectionValidationMessage(summarizeValidationMessage(response.data.validation));
      }
    } catch (err) {
      console.error('生成处方失败:', err);
      if (isAxiosError(err) && err.response?.status === 400) {
        const detail = err.response.data?.detail as
          | { validation?: PrescriptionValidationPayload; message?: string }
          | undefined;
        if (detail?.validation) {
          setSelectionValidation(detail.validation);
          setSelectionValidationMessage(summarizeValidationMessage(detail.validation));
          setError(detail.message ?? '所选药材存在禁忌，请调整后再试');
          return;
        }
      }
      setError('生成处方失败，请稍后再试');
    } finally {
      setPrescriptionLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedHerbs([]);
    setGeneratedPrescription(null);
    setIsConvertingRecipe(false);
    setError(null);
    setHasRecipePreview(false);
    setRecipePreviewTitle(null);
  setSelectionValidationBlockMessage(null);
    setSelectionValidation(null);
    setSelectionValidationMessage(null);
    setSelectionValidationLoading(false);
    validationRequestIdRef.current += 1;
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(GENERATED_RECIPE_PREVIEW_KEY);
      } catch (storageError) {
        console.warn('清理药膳预览缓存失败', storageError);
      }
    }
  };

  // 将药方转换为药膳
  const convertToRecipe = async () => {
    if (!generatedPrescription?.prescription_text) {
      setError('暂无可转换的处方内容');
      return;
    }

    setIsConvertingRecipe(true);
    setError(null);

    try {
      const herbNames =
        generatedPrescription.herbs?.map((item) => item.name || '').filter(Boolean) ?? [];

      const response = await apiClient.post('/prescription_to_recipe', {
        prescription: generatedPrescription.prescription_text,
        herbs: herbNames,
        patient_info: ''
      });

      const recipeContent: string = response.data.recipe_content;
      const derivedTitle = extractRecipeTitle(recipeContent, generatedPrescription.title);

      const previewPayload = {
        recipeTitle: derivedTitle,
        recipeContent,
        herbs: generatedPrescription.herbs ?? [],
        diagnosis: generatedPrescription.diagnosis ?? null,
        sourcePrescriptionId: generatedPrescription.prescription_id ?? null,
        sourcePrescriptionTitle: generatedPrescription.title ?? null,
        createdAt: new Date().toISOString(),
      };

      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(
            GENERATED_RECIPE_PREVIEW_KEY,
            JSON.stringify(previewPayload),
          );
        } catch (storageError) {
          console.warn('缓存药膳预览数据失败', storageError);
        }
      }

      setHasRecipePreview(true);
      setRecipePreviewTitle(derivedTitle);
    } catch (err) {
      console.error('药方转药膳失败:', err);
      setError('药方转药膳失败，请稍后再试');
    } finally {
      setIsConvertingRecipe(false);
    }
  };

  if (isLoading) {
    return (
      <div className="unified-page-wrapper">
        <div className="unified-page-header">
          <h2>中药材库</h2>
        </div>
        <div className="unified-page-content-scrollable">
          <div className="loading-container">加载中...</div>
        </div>
      </div>
    );
  }

  if (error && herbs.length === 0) {
    return (
      <div className="unified-page-wrapper">
        <div className="unified-page-header">
          <h2>中药材库</h2>
        </div>
        <div className="unified-page-content-scrollable">
          <div className="error-container">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="unified-page-wrapper herb-library-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="unified-page-header herb-hero">
        <div className="hero-text">
          <span className="hero-badge">Herb Atlas</span>
          <h2>中药材智选库</h2>
          <p className="hero-sub">精选经典本草，智能助力辨证组方</p>
          <div className="hero-tags">
            <span>AI辅助配伍</span>
            <span>功效速览</span>
            <span>临证灵活调剂</span>
          </div>
        </div>
        <div className="hero-stats">
          {heroStats.map((stat) => (
            <div key={stat.label} className="hero-stat-card">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="unified-page-content-scrollable herb-scroll">
        <div className="herb-layout">
          <aside className="herb-panel">
            <motion.div
              className="panel-card search-panel"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="panel-header">
                <h3>快速检索</h3>
                <span className="panel-caption">支持名称、功效、主治症状</span>
              </div>
              <div className="search-stack">
                <div className="search-input-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (effectKeyword) setEffectKeyword(null);
                    }}
                    placeholder="搜索中药材名称或功效..."
                    className="search-input"
                  />
                </div>
                <div className="filter-row">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="category-select"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? '所有分类' : category}
                      </option>
                    ))}
                  </select>
                  <div className="view-toggle-group" aria-label="切换展示视图">
                    <button
                      type="button"
                      className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                    >
                      ⬚
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      ☰
                    </button>
                  </div>
                </div>
              </div>

              {curatedCategories.length > 0 && (
                <div className="quick-pills">
                  {curatedCategories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      className={`pill ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => handleCategoryPreset(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}

              <div className="insight-row">
                <div>
                  <span className="insight-label">筛选结果</span>
                  <strong>{filteredHerbs.length}</strong>
                </div>
                <div>
                  <span className="insight-label">已选择</span>
                  <strong>{selectedHerbs.length}</strong>
                </div>
              </div>
            </motion.div>

            {functionCloud.length > 0 && (
              <motion.div
                className="panel-card function-panel"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="panel-header">
                  <h3>热门功效热词</h3>
                  <span className="panel-caption">点击关键词快速锁定相关药材</span>
                </div>
                <div className="function-cloud">
                  {functionCloud.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      className={`function-chip ${effectKeyword === item.name ? 'active' : ''}`}
                      onClick={() => handleEffectKeyword(item.name)}
                    >
                      <span>{item.name}</span>
                      <small>{item.count}</small>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {selectedHerbs.length > 0 && (
              <motion.div
                className="panel-card selection-panel"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="panel-header">
                  <h3>配方草稿</h3>
                  <span className="panel-caption">点击卡片可移除药材</span>
                </div>
                {selectionValidationLoading && (
                  <div className="selection-validation-banner loading">正在校验配伍...</div>
                )}
                {!selectionValidationLoading && selectionValidationBlockMessage && (
                  <div className="selection-validation-banner fail">
                    <strong>{VALIDATION_STATUS_LABELS.fail}</strong>
                    <span>{selectionValidationBlockMessage}</span>
                  </div>
                )}
                {!selectionValidationLoading && selectionValidation && selectionValidation.status !== 'pass' && (
                  <div className={`selection-validation-banner ${selectionValidation.status}`}>
                    <strong>{VALIDATION_STATUS_LABELS[selectionValidation.status]}</strong>
                    {selectionValidationMessage && <span>{selectionValidationMessage}</span>}
                  </div>
                )}
                {!selectionValidationLoading && selectionValidation && selectionValidation.status === 'pass' && selectionValidationMessage && (
                  <div className="selection-validation-banner pass">
                    <strong>{VALIDATION_STATUS_LABELS.pass}</strong>
                    <span>{selectionValidationMessage}</span>
                  </div>
                )}
                {!selectionValidationLoading && !selectionValidation && selectionValidationMessage && (
                  <div className="selection-validation-banner warning">
                    <span>{selectionValidationMessage}</span>
                  </div>
                )}
                <div className="selected-herbs-list">
                  {selectedHerbs.map(herb => (
                    <button
                      key={herb.id}
                      className="selected-herb-tag"
                      onClick={() => { void toggleHerbSelection(herb); }}
                    >
                      {herb.name}
                      <span className="remove-icon">×</span>
                    </button>
                  ))}
                </div>
                <div className="selection-actions">
                  <button
                    onClick={generatePrescription}
                    disabled={
                      prescriptionLoading ||
                      selectionValidationLoading ||
                      selectionValidation?.status === 'fail'
                    }
                    className="generate-btn"
                  >
                    {prescriptionLoading ? '生成中...' : 'AI生成药方'}
                  </button>
                  <button onClick={clearSelection} className="clear-btn">清空</button>
                </div>
                {selectionValidation?.status === 'fail' && selectionValidationMessage && (
                  <p className="selection-validation-hint">{selectionValidationMessage}</p>
                )}
              </motion.div>
            )}
          </aside>

          <main className="herb-main">
            {error && <div className="error-message">{error}</div>}

            {generatedPrescription && (
              <motion.div
                className="prescription-result premium-card"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="prescription-header">
                  <div>
                    <span className="mini-badge">AI Recommend</span>
                    <h3>智能配方建议</h3>
                  </div>
                  <div className="prescription-header-actions">
                    <button
                      onClick={convertToRecipe}
                      className="convert-to-recipe-btn"
                      disabled={isConvertingRecipe || !generatedPrescription.prescription_text}
                    >
                      {isConvertingRecipe ? '转换中...' : '🥣 转为药膳'}
                    </button>
                  </div>
                </div>

                <div className="prescription-summary-block">
                  <p>
                    已为您生成配方
                    <strong>《{generatedPrescription.title}》</strong>
                    ，共含 {generatedPrescription.herbs?.length ?? 0} 味药材。
                  </p>
                  {generatedPrescription.diagnosis && (
                    <p className="summary-diagnosis">辨证要点：{generatedPrescription.diagnosis}</p>
                  )}
                  {generatedPrescription.herbs?.length ? (
                    <ul className="summary-herb-list">
                      {generatedPrescription.herbs.slice(0, 4).map((herb) => (
                        <li key={`${herb.name}-${herb.dosage}`.trim()}>
                          <span className="herb-name">{herb.name}</span>
                          {herb.dosage && <span className="herb-dose">{herb.dosage}</span>}
                          {herb.reason && <span className="herb-reason">{herb.reason}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="summary-placeholder">暂无药材详情。</p>
                  )}
                </div>

                <div className="prescription-actions">
                  <button
                    type="button"
                    className="primary-link-btn"
                    onClick={() => navigate(`/app/prescriptions/${generatedPrescription.prescription_id}`)}
                  >
                    查看药方详情
                  </button>
                  <button
                    type="button"
                    className="secondary-link-btn"
                    onClick={() => {
                      if (generatedPrescription.url) {
                        window.open(generatedPrescription.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    新标签页打开
                  </button>
                </div>
              </motion.div>
            )}

            {hasRecipePreview && (
              <motion.div
                className="recipe-preview-card premium-card"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3>🥣 药膳详情已就绪</h3>
                {recipePreviewTitle && (
                  <p className="recipe-preview-meta">最新生成：{recipePreviewTitle}</p>
                )}
                <p className="recipe-preview-note">点击下方按钮在专门的详情页查看完整的药膳制作步骤与功效解析。</p>
                <div className="recipe-preview-actions">
                  <button
                    type="button"
                    className="primary-link-btn"
                    onClick={() => navigate('/app/dietary-recipes/generated')}
                  >
                    查看药膳详情
                  </button>
                </div>
              </motion.div>
            )}

            <motion.div
              className="results-toolbar"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div>
                <h3>药材列表</h3>
                <p>点击药材卡片快速加入配方</p>
              </div>
              <div className="toolbar-meta">
                <span>共 {filteredHerbs.length} 条结果</span>
                {selectedCategory !== 'all' && (
                  <span className="meta-tag">分类：{selectedCategory}</span>
                )}
                {effectKeyword && (
                  <span className="meta-tag">功效：{effectKeyword}</span>
                )}
              </div>
            </motion.div>

            <motion.div
              className={`herbs-grid ${viewMode}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {filteredHerbs.length > 0 ? (
                paginatedHerbs.map(herb => {
                  const isSelected = selectedHerbs.some(h => h.id === herb.id);
                  return (
                    <div
                      key={herb.id}
                      className={`herb-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => { void toggleHerbSelection(herb); }}
                    >
                      <div className="herb-card-header">
                        <div>
                          <h4 className="herb-name">{herb.name}</h4>
                          <p className="herb-flavor">性味 · {herb.properties}性 · {herb.flavor}</p>
                        </div>
                        <span className="herb-category">{herb.category}</span>
                      </div>

                      <div className="herb-functions">
                        <span className="section-title">核心功效</span>
                        <ul>
                          {herb.functions.slice(0, 3).map((func, index) => (
                            <li key={index}>{func}</li>
                          ))}
                        </ul>
                        {herb.functions.length > 3 && (
                          <div className="more-indicator">等 {herb.functions.length} 项功效…</div>
                        )}
                      </div>

                      <div className="herb-dosage">
                        <span className="section-title">参考用量</span>
                        <p>{herb.dosage || '依证酌情加减'}</p>
                      </div>

                      {herb.indications?.length > 0 && (
                        <div className="herb-indications">
                          <span className="section-title">常见主治</span>
                          <div className="indication-tags">
                            {herb.indications.slice(0, 3).map((item, idx) => (
                              <span key={idx}>{item}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {isSelected && <div className="selected-indicator">✓ 已加入配方</div>}
                    </div>
                  );
                })
              ) : (
                <div className="no-results">
                  <p>没有找到符合条件的中药材，试试调整筛选条件。</p>
                </div>
              )}
            </motion.div>

            {filteredHerbs.length > PAGE_SIZE && (
              <div className="pagination-bar">
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => gotoPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  上一页
                </button>
                <div className="pager-info">
                  第 <strong>{currentPage}</strong> / {totalPages} 页
                </div>
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => gotoPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  下一页
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </motion.div>
  );
};

export default HerbLibrary;